import React, { useEffect, useMemo, useRef, useState } from 'react';
import { filterCountries, getCountryByCode } from '../../../utils/countries';

/**
 * Searchable country dropdown (extracted from CompanyInformationStep so the Identity step and the
 * regularization form share one implementation).
 */
const CountrySelect = ({
  value,
  onChange,
  error,
  label = 'Country of Origin',
  required = true,
  disabled = false,
  description,
  highlight = false,
  // Spread onto the trigger button, e.g. fieldProps('countryOrigin'), so the form can scroll to it.
  triggerProps = {},
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const filtered = useMemo(() => filterCountries(searchTerm), [searchTerm]);
  const selected = getCountryByCode(value);
  const listRef = useRef(null);

  // The list opens near the bottom of a short step; bring it fully into view instead of
  // leaving half of it under the sticky footer.
  useEffect(() => {
    if (open) listRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [open]);

  const choose = (code) => {
    onChange(code);
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      <label htmlFor={triggerProps.id} className="block text-sm font-medium text-foreground mb-2">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
      <div className="relative">
        <button
          {...triggerProps}
          type="button"
          onClick={() => !disabled && setOpen(!open)}
          disabled={disabled}
          className={`flex h-11 w-full items-center px-3 pr-9 text-left text-base border rounded-md bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-muted/60 ${
            error ? 'border-destructive' : highlight ? 'border-success/40 bg-success/5' : 'border-border'
          } ${value ? 'text-foreground' : 'text-muted-foreground'}`}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selected ? selected.label : 'Select a country'}
        </button>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {open && (
        <>
          {/* Above the form's sticky footer (z-50), which otherwise paints over the list. */}
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div ref={listRef} className="absolute z-[70] w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-border">
              <input
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox">
              {filtered.length > 0 ? (
                filtered.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    role="option"
                    aria-selected={value === c.value}
                    onClick={() => choose(c.value)}
                    className={`w-full px-4 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none ${
                      value === c.value ? 'bg-primary text-white hover:bg-primary/90' : ''
                    }`}
                  >
                    {c.label}
                  </button>
                ))
              ) : (
                <div className="px-4 py-2 text-sm text-muted-foreground">No countries found</div>
              )}
            </div>
          </div>
        </>
      )}

      {description && !error && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
};

export default CountrySelect;
